// /refund-policy — mirrors the policy at jobtest.org/refund-policy so the
// money-back-guarantee link on /pricing resolves on this subdomain.
// NOTE: legal text — verify wording matches the canonical source before relying on it.

const SECTIONS: { heading: string; body: React.ReactNode }[] = [
  {
    heading: '1. General Refund Conditions',
    body: (
      <ul>
        <li>All refund requests must be made within 7 days of purchase. After 7 days, refund eligibility is at our discretion.</li>
        <li>A 10% administrative fee will be deducted from eligible refunds, applied at the company's discretion.</li>
      </ul>
    ),
  },
  {
    heading: '2. Reports and Digital Products',
    body: (
      <>
        <p>We offer a satisfaction guarantee on reports, with refund amounts based on how much of the content has been consumed:</p>
        <ul>
          <li>Less than 25% consumed: full refund upon request.</li>
          <li>25–50% consumed: 50% refund.</li>
          <li>Over 50% consumed: not eligible unless a documented issue exists.</li>
        </ul>
        <p>Additional requirements include completing a satisfaction-resolution process and providing brief feedback on the reason for dissatisfaction.</p>
      </>
    ),
  },
  {
    heading: '3. AI Resume Builder',
    body: (
      <>
        <p>Applies to JobReadyResume.org Lifetime or other access offers. Treated as a final sale due to immediate digital delivery.</p>
        <p><strong>Refund eligibility (all must be true):</strong></p>
        <ul>
          <li>No resumes created or downloaded after purchase.</li>
          <li>Request submitted within 7 days of the purchase date.</li>
          <li>The user completed account-setup instructions sent via email.</li>
        </ul>
        <p><strong>Non-refundable situations:</strong> unintentional purchases, any resume-content creation, requests after 7 days, failure to complete setup, or post-access refund attempts.</p>
        <p><strong>Refund request process:</strong> email hello@jobreadyresume.org with your checkout email, order ID/receipt, and confirmation that no resume was created. Support responds within 2–3 business days.</p>
      </>
    ),
  },
  {
    heading: '4. Coaching Services',
    body: (
      <ul>
        <li>Free rematch with a different coach if you're dissatisfied or a coach doesn't attend.</li>
        <li>Full refund eligible after two unsuccessful rematches.</li>
        <li>Subscriptions auto-renew unless canceled beforehand via the client portal.</li>
        <li>No refunds are issued for renewal charges resulting from a failure to cancel.</li>
        <li>24-hour cancellation/rescheduling policy; credits are forfeited if canceled within 24 hours or a no-show occurs.</li>
      </ul>
    ),
  },
  {
    heading: '5. Money-Back Guarantee Policy',
    body: (
      <p>
        Applies to paid reports and coaching services within 7 days of purchase. Coaching refunds require two rematches first; report refunds require a satisfaction-resolution attempt. The company reserves the right to limit future refunds for frequent requesters.
      </p>
    ),
  },
  {
    heading: '6. Processing Times and Methods',
    body: (
      <p>Refunds will be processed within 5–7 business days and issued to the original payment method.</p>
    ),
  },
  {
    heading: '7. Contact Information',
    body: (
      <p>
        For refund requests or questions, contact customer support at{' '}
        <a href="mailto:support@jobtest.org" className="text-blue-600 underline hover:text-blue-700">support@jobtest.org</a>.
      </p>
    ),
  },
];

export function RefundPolicy() {
  return (
    <div className="min-h-dvh bg-canvas text-ink">
      <header className="border-b border-ink/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-4">
          <a href="/" aria-label="Jobtest.org">
            <img src="/jobtestlogo.svg" alt="Jobtest.org" className="h-7 w-auto" width={319} height={76} />
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-10 sm:py-14">
        <h1 className="text-display-md text-ink leading-tight mb-2">Refund Policy</h1>
        <p className="text-sm text-ink-soft mb-10">Last updated: 11/05/2024</p>

        <div className="space-y-9">
          {SECTIONS.map((s) => (
            <section key={s.heading}>
              <h2 className="text-lg font-extrabold text-ink mb-3">{s.heading}</h2>
              <div className="text-ink-soft leading-relaxed space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_li]:leading-relaxed">
                {s.body}
              </div>
            </section>
          ))}
        </div>

        <p className="mt-12 pt-6 border-t border-ink/10 text-sm text-ink-soft">
          <a href="/pricing" className="text-blue-600 underline hover:text-blue-700">← Back to pricing</a>
        </p>
      </main>
    </div>
  );
}
